import { useState, useContext } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bomb, Gem, RotateCcw, History } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export const MinesPage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [betAmount, setBetAmount] = useState(100);
  const [mineCount, setMineCount] = useState(5);
  const [isPlaying, setIsPlaying] = useState(false);
  const [grid, setGrid] = useState(Array(25).fill({ revealed: false, isMine: false }));
  const [multiplier, setMultiplier] = useState(1);
  const [currentBetId, setCurrentBetId] = useState(null);
  const [revealedCount, setRevealedCount] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [history, setHistory] = useState([]);

  const calculateMultiplier = (revealed, mines) => {
    const safeSpots = 25 - mines;
    let mult = 1;
    for (let i = 0; i < revealed; i++) {
      mult *= safeSpots / (safeSpots - i);
    }
    return Math.min(mult * 0.97, 1000); // 3% house edge, max 1000x
  };

  const startGame = async () => {
    if (betAmount <= 0 || betAmount > (user?.balance || 0)) {
      toast.error("Invalid bet amount");
      return;
    }

    try {
      // Deduct bet from balance
      await axios.post(`${API}/games/dice/play`, {
        game_type: "mines_start",
        bet_amount: betAmount,
        bet_data: { mine_count: mineCount }
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });

      // Generate mine positions
      const minePositions = new Set();
      while (minePositions.size < mineCount) {
        minePositions.add(Math.floor(Math.random() * 25));
      }

      const newGrid = Array(25).fill(null).map((_, i) => ({
        revealed: false,
        isMine: minePositions.has(i)
      }));

      setGrid(newGrid);
      setIsPlaying(true);
      setGameOver(false);
      setMultiplier(1);
      setRevealedCount(0);
      setCurrentBetId(`mines_${Date.now()}`);
      await refreshBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start game");
    }
  };

  const revealTile = (index) => {
    if (!isPlaying || grid[index].revealed || gameOver) return;

    const newGrid = [...grid];
    newGrid[index] = { ...newGrid[index], revealed: true };
    setGrid(newGrid);

    if (newGrid[index].isMine) {
      // Hit a mine - game over
      setGameOver(true);
      setIsPlaying(false);
      // Reveal all mines
      const revealedGrid = newGrid.map(tile => 
        tile.isMine ? { ...tile, revealed: true } : tile
      );
      setGrid(revealedGrid);
      setHistory(prev => [{ won: false, mines: mineCount, revealed: revealedCount, amount: betAmount }, ...prev.slice(0, 9)]);
      toast.error("BOOM! You hit a mine!");
    } else {
      // Safe tile
      const newRevealed = revealedCount + 1;
      setRevealedCount(newRevealed);
      const newMult = calculateMultiplier(newRevealed, mineCount);
      setMultiplier(newMult);
      
      // Check if all safe tiles revealed (auto cashout)
      const safeTiles = 25 - mineCount;
      if (newRevealed === safeTiles) {
        cashOut(newMult);
      }
    }
  };

  const cashOut = async (finalMult = multiplier) => {
    if (!isPlaying || revealedCount === 0) return;

    try {
      const winnings = betAmount * finalMult;
      
      await axios.post(`${API}/wallet/deposit`, {
        amount: winnings,
        transaction_type: "win"
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });

      setIsPlaying(false);
      setHistory(prev => [{ won: true, mines: mineCount, revealed: revealedCount, amount: winnings, mult: finalMult.toFixed(2) }, ...prev.slice(0, 9)]);
      toast.success(`Cashed out ₹${winnings.toFixed(2)} at ${finalMult.toFixed(2)}x!`);
      await refreshBalance();
    } catch (error) {
      toast.error("Failed to cash out");
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-[#FF6B00]/20 flex items-center justify-center">
            <Bomb className="w-6 h-6 text-[#FF6B00]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-['Rajdhani']" data-testid="mines-title">Mines</h1>
            <p className="text-[#A1A1AA] text-sm">Reveal gems, avoid mines!</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Game Area */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl bg-[#1A1B1F] border border-white/[0.08] p-6">
              {/* Multiplier Display */}
              <div className="text-center mb-6">
                <span className="text-[#A1A1AA] text-sm">Current Multiplier</span>
                <div className={`text-5xl font-bold font-mono ${gameOver ? 'text-[#FF3B30]' : 'text-[#00E701]'}`}>
                  {multiplier.toFixed(2)}x
                </div>
                {isPlaying && revealedCount > 0 && (
                  <span className="text-[#FFD600] text-lg font-mono">
                    Potential: ₹{(betAmount * multiplier).toFixed(2)}
                  </span>
                )}
              </div>

              {/* Grid */}
              <div className="grid grid-cols-5 gap-2 max-w-md mx-auto mb-6">
                {grid.map((tile, index) => (
                  <button
                    key={index}
                    onClick={() => revealTile(index)}
                    disabled={!isPlaying || tile.revealed || gameOver}
                    className={`
                      aspect-square rounded-lg flex items-center justify-center text-2xl
                      transition-all duration-200 transform hover:scale-105
                      ${tile.revealed 
                        ? tile.isMine 
                          ? 'bg-[#FF3B30] scale-110' 
                          : 'bg-[#00E701]/20 border-[#00E701]/50'
                        : 'bg-white/10 hover:bg-white/20 border-white/10'
                      }
                      border-2
                    `}
                    data-testid={`mine-tile-${index}`}
                  >
                    {tile.revealed && (
                      tile.isMine ? <Bomb className="w-6 h-6 text-white" /> : <Gem className="w-6 h-6 text-[#00E701]" />
                    )}
                  </button>
                ))}
              </div>

              {/* Controls */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#A1A1AA] text-sm mb-2 block">Bet Amount (₹)</label>
                    <Input
                      type="number"
                      value={betAmount}
                      onChange={(e) => setBetAmount(Number(e.target.value))}
                      disabled={isPlaying}
                      className="h-12 bg-black/20 border-white/10 text-white font-mono"
                      data-testid="mines-bet-input"
                    />
                  </div>
                  <div>
                    <label className="text-[#A1A1AA] text-sm mb-2 block">Mines ({mineCount})</label>
                    <input
                      type="range"
                      min="1"
                      max="24"
                      value={mineCount}
                      onChange={(e) => setMineCount(Number(e.target.value))}
                      disabled={isPlaying}
                      className="w-full h-12 accent-[#FF6B00]"
                    />
                  </div>
                </div>

                {!isPlaying ? (
                  <Button
                    onClick={startGame}
                    disabled={betAmount <= 0 || betAmount > (user?.balance || 0)}
                    className="w-full h-14 bg-[#00E701] hover:bg-[#00C201] text-black font-bold text-lg"
                    data-testid="mines-start-btn"
                  >
                    Start Game
                  </Button>
                ) : (
                  <Button
                    onClick={() => cashOut()}
                    disabled={revealedCount === 0}
                    className="w-full h-14 bg-[#FFD600] hover:bg-[#E6C200] text-black font-bold text-lg"
                    data-testid="mines-cashout-btn"
                  >
                    Cash Out ₹{(betAmount * multiplier).toFixed(2)}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Info */}
            <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
              <h3 className="text-lg font-bold text-white font-['Rajdhani'] mb-4">How to Play</h3>
              <ul className="space-y-2 text-[#A1A1AA] text-sm">
                <li>• Set your bet and number of mines</li>
                <li>• Click tiles to reveal gems</li>
                <li>• More mines = higher multipliers</li>
                <li>• Cash out anytime before hitting a mine!</li>
              </ul>
            </div>

            {/* History */}
            <div className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08]">
              <div className="flex items-center gap-2 mb-4">
                <History className="w-5 h-5 text-[#A1A1AA]" />
                <h3 className="text-lg font-bold text-white font-['Rajdhani']">History</h3>
              </div>
              <div className="space-y-2">
                {history.length > 0 ? history.map((h, idx) => (
                  <div key={idx} className={`p-2 rounded-lg text-sm ${h.won ? 'bg-[#00E701]/10' : 'bg-[#FF3B30]/10'}`}>
                    <div className="flex justify-between">
                      <span className={h.won ? 'text-[#00E701]' : 'text-[#FF3B30]'}>
                        {h.won ? `+₹${h.amount?.toFixed(0)}` : `-₹${h.amount}`}
                      </span>
                      <span className="text-white/50">{h.mines} mines</span>
                    </div>
                  </div>
                )) : (
                  <p className="text-[#52525B] text-sm text-center">No games yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
